import { DateTime } from 'luxon';
import { IST_ZONE } from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE USAGE-HOURS HEATMAP, AS NUMBERS. [ANALYTICS §5]
 *
 *  Pure, and it takes the wire's two arrays and the window's two ends rather than the DTO, so the page, the grid and its
 *  sentence all read ONE reduction — a busiest cell computed twice is two answers the day one
 *  of them changes its tie-break.
 *
 *  ⚠️ AVERAGE OR TOTAL IS DECIDED FROM THE DATA, NEVER FROM THE WINDOW'S NAME. `datesPerWeekday`
 *  says how many IST dates of each weekday the window covers; a cell is read as a daily AVERAGE
 *  only when every weekday is covered at least twice. Below that — the 30-minute, 1-day and 7-day
 *  windows, where a weekday is one date or none — an "average Tuesday" is one Tuesday, and saying
 *  "average" would claim a pattern the window cannot show. The sentence says which it is.
 *
 *  ⚠️ AND A WINDOW WITH A CAPTURE GAP IS READ AS TOTALS TOO. An average divides by every date the
 *  window covers, so a date the counters did not capture would be averaged in as a quiet one —
 *  the confident wrong number `coverage` exists to prevent. The caller says whether it is whole.
 *
 *  ⚠️ AN AVERAGE DIVIDES EACH HOUR BY THE DATES THAT HOUR WAS PART OF THE WINDOW — NOT BY
 *  `datesPerWeekday`. The server counts every IST date from the window's start through TODAY, and
 *  today is not over: at 10:30 on a Tuesday, a thirty-day window's Tuesday 22:00 cell holds four
 *  Tuesdays of requests, and dividing it by five understates it by a fifth — enough to move "Busiest" to another weekday. So
 *  an hour today has not reached drops today's date from its divisor, and an hour before the
 *  window's start on its first date drops that one. ⚠️ The CURRENT hour keeps today's date though
 *  it is only partly over, so its average is slightly low until it ends; the basis caption says
 *  today counts only towards the hours that have begun.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ FIVE LITERAL CLASSES, INDEXED BY LEVEL — NEVER `bg-foreground/${n}`. Tailwind extracts
 * candidates from SOURCE TEXT, so an interpolated class generates nothing and every cell renders
 * blank with `tsc`, `eslint` and `vitest` all green (`dashboardGrid.ts`'s trap, one file over).
 *
 * ⚠️ `foreground`, NOT `data-bar`, AND THE REASON IS MEASURED. `data-bar` is already a WASH — the
 * brand purple at 12% in the light palette and white at 8% in dark — because `RankedList` sets
 * text on top of it. An opacity modifier multiplies that alpha, so a four-step ramp over it runs
 * from about 3.6% to 12% and adjacent steps measure 1.04–1.06:1 against each other: a heatmap
 * nobody can read. No text sits on a cell, so the constraint that made `data-bar` faint does not
 * apply here: over the neutral ink the smallest adjacent step is 1.21:1 (colourful light) and
 * every step clears 1.2 in all four palette-and-mode pairs — `theme/analyticsTint.test.ts` holds
 * the floor, with the `data-bar` ramp kept as its negative control.
 *
 * ⚠️ LIGHTNESS IS THE WHOLE ENCODING (R1): one ink at rising strength, so the ramp survives
 * greyscale untouched and spends no hue — red, amber and green mean fulfilment in this product.
 */
export const HEAT_LEVEL_CLASSES = [
  'bg-muted',
  'bg-foreground/15',
  'bg-foreground/30',
  'bg-foreground/50',
  'bg-foreground/75',
] as const;

export type HeatLevel = 0 | 1 | 2 | 3 | 4;

/** The IST hours counted as daytime in the sentence: 08:00 up to, not including, 20:00. */
export const DAYTIME_FROM_HOUR = 8;
export const DAYTIME_TO_HOUR = 20;

export type UsageHoursBasis = 'average' | 'total';

/** The window's two ends as the wire sends them: `from`, and `to`, which is when the report was generated. */
export type UsageHoursSpan = { from: string; to: string };

/** An instant's IST weekday, Monday 0, and IST hour — the grid's own coordinates. */
const istCellOf = (instant: string): { weekday: number; hour: number } => {
  const at = DateTime.fromISO(instant, { zone: IST_ZONE });
  return { weekday: at.weekday - 1, hour: at.hour };
};

export type UsageHoursReading = {
  basis: UsageHoursBasis;
  /** What each cell shows — the total, or that total divided by its weekday's dates. */
  values: number[][];
  levels: HeatLevel[][];
  /** The highest cell; the earliest weekday and hour on a tie. `null` when nothing was counted. */
  busiest: { weekday: number; hour: number; value: number } | null;
  /** Every request the grid counts, whatever the basis. */
  total: number;
  /** Requests between `DAYTIME_FROM_HOUR` and `DAYTIME_TO_HOUR`, over `total`. `null` when `total` is 0. */
  daytimeShare: number | null;
};

/**
 * A value's level against the highest one: `0` for nothing, then the quarter of the maximum it
 * falls in, so any non-zero cell is at least level 1 and a cell with one request never reads as
 * an empty one.
 */
export const heatLevelOf = (value: number, max: number): HeatLevel => {
  if (value <= 0 || max <= 0) return 0;
  const quarter = Math.ceil((value / max) * 4);
  return Math.min(4, Math.max(1, quarter)) as HeatLevel;
};

/**
 * ⚠️ `span` IS REQUIRED, NOT DEFAULTED. Omitted, every hour would divide by the whole
 * `datesPerWeekday` — the partial-today understatement above, shipped silently.
 */
export const readUsageHours = (
  cells: readonly (readonly number[])[],
  datesPerWeekday: readonly number[],
  wholeWindow: boolean,
  span: UsageHoursSpan,
): UsageHoursReading => {
  const basis: UsageHoursBasis =
    wholeWindow && datesPerWeekday.length === 7 && datesPerWeekday.every((dates) => dates >= 2) ? 'average' : 'total';
  const first = istCellOf(span.from);
  const last = istCellOf(span.to);
  /* The dates this weekday-and-hour was inside the window: a date the hour is after `to`, or before `from`, on is not one. */
  const datesCovering = (weekday: number, hour: number): number =>
    (datesPerWeekday[weekday] ?? 0) -
    (weekday === last.weekday && hour > last.hour ? 1 : 0) -
    (weekday === first.weekday && hour < first.hour ? 1 : 0);

  let total = 0;
  let daytime = 0;
  const values = cells.map((row, weekday) =>
    row.map((requests, hour) => {
      total += requests;
      if (hour >= DAYTIME_FROM_HOUR && hour < DAYTIME_TO_HOUR) daytime += requests;
      if (basis === 'total') return requests;
      const dates = datesCovering(weekday, hour);
      return dates > 0 ? requests / dates : 0;
    }),
  );

  /* Strictly greater, walked Monday first and midnight first, which is what makes a tie the earliest. */
  let busiest: UsageHoursReading['busiest'] = null;
  for (const [weekday, row] of values.entries()) {
    for (const [hour, value] of row.entries()) {
      if (value > 0 && (busiest === null || value > busiest.value)) busiest = { weekday, hour, value };
    }
  }
  const max = busiest?.value ?? 0;

  return {
    basis,
    values,
    levels: values.map((row) => row.map((value) => heatLevelOf(value, max))),
    busiest,
    total,
    daytimeShare: total === 0 ? null : daytime / total,
  };
};
